import React from 'react';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import Bullets from 'slickpay/common/Bullets.component';

import IMG_DIAGRAM from 'assets/images/stripe/commission-diagram.png';
import Permitted from 'components/core/Permitted.component';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import PERM from 'constants/core/perm.const';
import LINK from 'constants/link.const';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';

import CN from './StaffCommissions.module.scss';

const TC_LINK = 'https://www.getslick.com/legal/terms';
const LEARN_LINK = 'https://help.getslick.com/en/articles/10767655-split-commissions-faq-s';
const LINK_HELP = 'https://help.getslick.com/en/articles/11686307-split-takings-for-chair-renters';

const getBullets = () => [
  <div className={CN.bullet}>Enter the % takings the staff member should receive (not the business share)</div>,
  <div className={CN.bullet}>
    Any category where the box is blank for % takings will be treated as 0% share for the staff member, so all takings
    in that category will go to the business.
  </div>,

  <div className={CN.bullet}>
    By activating <div className={CN.textBold}>SlickPay Split Takings</div>, you take full responsibility for ensuring %
    takings for each staff member are entered accurately for your business needs.
  </div>,
  <div className={CN.bullet}>
    As per our
    <ExternalLink link={TC_LINK} variant="inline">
      <span className={CN.extrLink}>Terms & Conditions</span>
    </ExternalLink>
    , Slick accepts no liability for any errors arising as a result of incomplete or incorrectly entered amounts for
    staff % split takings.
  </div>,
];

const CommissionsToolbar = () => {
  const { isDarkMode } = useTheme();
  const salonId = useCurrentSalonId();

  const reportsCommissions = linkTo({ pattern: LINK.reportsCommissions, params: { salonId }, fault: '#' });

  return (
    <div data-bem="CommissionsToolbar" className={CN.toolbar}>
      <div
        data-bem="CommissionsToolbar__toolbar"
        className={cn({
          [CN.floatingToolbar]: true,
          [CN.dark]: isDarkMode,
        })}
      >
        <div data-bem="CommissionsToolbar__panel" className={CN.panel}>
          <div className={CN.flex}>
            <div className={CN.pageTitle}>
              <div className={CN.iconContainer}>
                <Icon variant="moneyPaper" width="xl" />
              </div>
              Set Split Takings
            </div>

            <div className={CN.toolbarButtonsContainer} data-bem="CommissionsToolbar__panel-buttons">
              <div className={CN.learnMoreContainer}>
                <ExternalLink link={LEARN_LINK} variant="inline">
                  <div data-bem="KpiSummary__button" className={CN.inner}>
                    <span className={CN.learnMore}>MORE INFO</span>
                  </div>
                </ExternalLink>
              </div>
              <Permitted value={PERM.canEditStaffCommissions} tag="CommissionsToolbar__can-edit-commissions">
                <NavLink to={reportsCommissions}>
                  <Button variant="primaryForce" onClick={alwaysNoop}>
                    <span className={CN.navLinkColor}>TAKINGS REPORT</span>
                  </Button>
                </NavLink>
              </Permitted>
            </div>
          </div>
          <div className={CN.toolbarFlex}>
            <div className={CN.toolbarBulletsContainer}>
              <Bullets data={getBullets()} empty />
            </div>
            <div className={CN.toolbarImgContainer}>
              <img src={IMG_DIAGRAM} className={CN.toolbarImg} alt="automate your payroll diagram" />
            </div>
          </div>
        </div>
      </div>
      <AlertPanel tag="CommissionsToolbar__alertPanel" variant="alert">
        <div className={CN.alert}>
          <div className={CN.alertTitle}>IMPORTANT VAT Handling</div>
          <div className={CN.alertBulletsContainer}>
            <div className={CN.bullet}>
              All revenue will be split with VAT INCLUDED (if your services already include VAT)
            </div>
            <div className={CN.bullet}>
              Example: 50/50 split = £50 inc VAT to staff member and £50 inc VAT to business
            </div>
            <div className={CN.bullet}>
              If you wish to split revenue EXCLUDING VAT please read this
              <ExternalLink link={LINK_HELP} variant="inline">
                <div className={CN.linkHelp}>Help Guide</div>
              </ExternalLink>
            </div>
          </div>
        </div>
      </AlertPanel>
    </div>
  );
};
export default CommissionsToolbar;
